import { useEffect, useState } from "react";

type RecentError = {
  jobId: string;
  action: string;
  eventTime: string;
  teamId?: string;
  teamName?: string;
  detail?: string;
};

type Props = { graphToken: string | null };

function fmt(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function Diagnostics({ graphToken }: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<RecentError[]>([]);
  const [fetchErr, setFetchErr] = useState<string | null>(null);

  const load = async () => {
    if (!graphToken) return;
    setLoading(true);
    setFetchErr(null);
    try {
      const res = await fetch("/api/my-errors", {
        headers: { "x-graph-token": graphToken }
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const body = (await res.json()) as { errors: RecentError[] };
      setErrors(body.errors);
    } catch (e) {
      setFetchErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && graphToken) load();
  }, [open, graphToken]);

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text).catch(() => undefined);
  };

  return (
    <div className="card">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer"
        }}
        onClick={() => setOpen(!open)}
      >
        <h2 style={{ margin: 0 }}>4. Diagnostics</h2>
        <button className="secondary" type="button">
          {open ? "Hide" : "Show"}
        </button>
      </div>

      {open && (
        <div style={{ marginTop: 16 }}>
          <div className="row" style={{ marginBottom: 12 }}>
            <span className="muted">
              Your last 50 server-side errors and client error reports.
            </span>
            <button
              className="secondary"
              type="button"
              onClick={load}
              disabled={loading}
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>

          {loading && <div className="loading-bar" />}

          {fetchErr && (
            <div className="error" style={{ marginBottom: 12 }}>
              {fetchErr}{" "}
              <button className="secondary" type="button" onClick={load} style={{ marginLeft: 8 }}>
                Retry
              </button>
            </div>
          )}

          {!loading && errors.length === 0 && !fetchErr && (
            <p className="muted">No errors recorded.</p>
          )}

          {errors.length > 0 && (
            <div className="team-table-wrap">
              <table className="team-table">
                <thead>
                  <tr>
                    <th style={{ width: 160 }}>When</th>
                    <th style={{ width: 130 }}>Action</th>
                    <th>Detail</th>
                    <th style={{ width: 150 }}>Error ID</th>
                  </tr>
                </thead>
                <tbody>
                  {errors.map((e, i) => (
                    <tr key={`${e.jobId}-${i}`}>
                      <td className="small">{fmt(e.eventTime)}</td>
                      <td className="small"><code>{e.action}</code></td>
                      <td className="small">
                        {e.teamName && <div><strong>{e.teamName}</strong></div>}
                        {e.detail && (
                          <div style={{ wordBreak: "break-word" }}>{e.detail}</div>
                        )}
                      </td>
                      <td>
                        <code className="small">{e.jobId.slice(0, 8)}…</code>
                        <button
                          type="button"
                          className="secondary"
                          onClick={() => copy(e.jobId)}
                          style={{ marginLeft: 4, padding: "2px 6px", fontSize: 11 }}
                          title="Copy full error ID"
                        >
                          Copy
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
